import React, { useEffect, useMemo, useRef, useState, useCallback, memo } from 'react';
import API from '../api';
import '../styles/global.css';
import { toast } from 'react-toastify';
import { useDebounce } from '../hooks/useDebounce';
import { usePharmacy } from '../context/PharmacyContext';

// --- Utility Functions (Outside Render) ---
const fmt = (num) =>
  (Number.isFinite(Number(num)) ? Number(Number(num)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00');

// --- Memoized Sub-Components ---
const Badge = memo(({ value, isMobile }) => {
  const isPayable = value < 0;
  const isSettled = value === 0;
  const absVal = Math.abs(value);

  let bg = '#e0f2fe', color = '#1880c2', text = 'Settled';
  if (isPayable) {
    bg = '#fef5f5'; color = '#991b1b'; text = `We owe Rs ${fmt(absVal)}`;
  } else if (!isSettled) {
    bg = '#f0fdf4'; color = '#166534'; text = `Advance/Owes us Rs ${fmt(absVal)}`;
  }

  return (
    <span style={{ 
      padding: isMobile ? '4px 8px' : '6px 12px', 
      background: bg, color, borderRadius: 6, 
      fontSize: isMobile ? 10 : 12, fontWeight: 600, 
      display: 'inline-block', letterSpacing: '0.3px' 
    }}>
      {text}
    </span>
  );
});

const MobilePartyCard = memo(({ party, index, isMobile, onOpenDetails, onEdit, onDelete }) => (
  <div 
    onClick={() => onOpenDetails(party)} 
    style={{ 
      background: '#fff', border: '1px solid #e1e8ed', borderRadius: 10, 
      padding: 16, marginBottom: 12, cursor: 'pointer', transition: 'all 0.2s', 
      boxShadow: '0 1px 3px rgba(0,0,0,0.02)' 
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
      <strong style={{ fontSize: 14, color: '#1a202c', fontWeight: 700 }}>{party.name}</strong>
      <span style={{ fontSize: 11, color: '#1880c2', fontWeight: 700 }}>#{index + 1}</span>
    </div>
    <div style={{ fontSize: 12, color: '#718096', marginBottom: 12 }}>{party.phone || 'No Phone'}</div>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <Badge value={party.balance} isMobile={isMobile} />
      <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 8 }}>
        <button 
          onClick={() => onEdit(party)} 
          style={{ padding: '6px 12px', background: '#e0f2fe', color: '#1880c2', border: '1px solid #bae6fd', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 11 }}
        >Edit</button>
        <button 
          onClick={() => onDelete(party._id)} 
          style={{ padding: '6px 12px', background: '#fef5f5', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 11 }}
        >Delete</button>
      </div>
    </div>
  </div>
));

export default function PartiesPage() {
  // ---------- state ----------
  
  // 1. Check for cached data for instant load
  const cachedParties = sessionStorage.getItem('partiesList');
  const [list, setList] = useState(cachedParties ? JSON.parse(cachedParties) : []);
  const [loading, setLoading] = useState(!cachedParties); // Only load if no cache

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [openingInvoiceNumber, setOpeningInvoiceNumber] = useState('');
  const [opening, setOpening] = useState(0);
  const [q, setQ] = useState('');
  const debouncedQ = useDebounce(q, 250);
  const [editId, setEditId] = useState(null);
  const [address, setAddress] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [registrationType, setRegistrationType] = useState('Unregistered');

  const { pharmacyDetails: myProfile } = usePharmacy();

  const [showModal, setShowModal] = useState(false);
  const [selectedParty, setSelectedParty] = useState(null);
  const [selectedTxs, setSelectedTxs] = useState([]);
  const [filterStatus, setFilterStatus] = useState('ALL');

  const [showPartyFormModal, setShowPartyFormModal] = useState(false);
  const [showTxForm, setShowTxForm] = useState(false);
  const [newTxType, setNewTxType] = useState('receipt');
  const [newTxAmount, setNewTxAmount] = useState('');
  const [newTxInvoice, setNewTxInvoice] = useState('');
  const [newTxNotes, setNewTxNotes] = useState('');
  const [newTxDate, setNewTxDate] = useState(new Date().toISOString().split('T')[0]);
  const [txSubmitting, setTxSubmitting] = useState(false);

  // ---------- responsive state ----------
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024);

  const ledgerPrintRef = useRef();
  const partyPrintRef = useRef();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth > 768 && window.innerWidth <= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ---------- fetch & compute ----------
  const fetchList = useCallback(async () => {
    try {
      const { data: parties } = await API.get('/parties', { params: { q: debouncedQ } });
      setList(parties);
      
      // 2. Cache the data, but ONLY if we aren't currently searching 
      // (so we don't accidentally cache a filtered search result as the main list)
      if (!debouncedQ) {
        sessionStorage.setItem('partiesList', JSON.stringify(parties));
      }

      if (selectedParty) {
        const fresh = parties.find(p => p._id === selectedParty._id);
        if (fresh) {
          setSelectedParty(fresh);
        }
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load parties');
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, selectedParty]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // ---------- Print Helpers ----------
  const printRefContent = useCallback((ref, title = 'Print') => {
    if (!ref?.current) {
      toast.info('Nothing to print');
      return;
    }
    try {
      const content = ref.current.innerHTML;
      const printWindow = window.open('', '', 'width=1000,height=800');
      printWindow.document.write(`
        <html>
          <head>
            <title>${title}</title>
            <style>
              body { font-family: Inter, Arial, sans-serif; padding: 20px; color: #111; }
              table { width: 100%; border-collapse: collapse; margin-top: 14px; }
              th, td { border: 1px solid #ddd; padding: 8px; }
              th { background: #f7fafc; }
            </style>
          </head>
          <body>${content}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    } catch {
      toast.error('Print failed');
    }
  }, []);

  const printConfirmation = useCallback(() => {
    if (!selectedParty) return;

    const printWindow = window.open('', '_blank');
    const today = new Date().toLocaleDateString('en-IN');
    const currentYear = new Date().getFullYear();
    const nextYear = currentYear + 1;
    const fiscalYear = `${currentYear}/${nextYear}`;

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Confirmation - ${selectedParty.name}</title>
        <style>
          body { font-family: 'Courier New', Courier, monospace; padding: 40px; color: #000; max-width: 900px; margin: 0 auto; }
          .header { text-align: center; margin-bottom: 40px; }
          .header h1 { margin: 0; font-size: 24px; font-weight: bold; text-transform: uppercase; }
          .header p { margin: 4px 0; font-size: 14px; }
          .meta-row { display: flex; justify-content: space-between; margin-bottom: 30px; border-bottom: 1px dashed #000; padding-bottom: 10px; }
          .to-section { margin-bottom: 30px; }
          .to-section h3 { margin: 0; font-size: 16px; font-weight: bold; }
          .to-section p { margin: 4px 0; font-size: 14px; }
          .subject { text-align: center; margin: 30px 0; font-weight: bold; text-decoration: underline; font-size: 16px; }
          .body-text { margin-bottom: 20px; line-height: 1.5; font-size: 14px; text-align: justify; }
          .account-table { width: 100%; border-collapse: collapse; margin: 20px 0; border: 1px dashed #000; }
          .account-table th, .account-table td { padding: 10px; border: 1px dashed #000; text-align: right; font-size: 14px; }
          .account-table th { text-align: left; }
          .footer { margin-top: 60px; display: flex; justify-content: space-between; font-size: 14px; }
          .footer-section { width: 40%; }
          .stamp-area { height: 80px; margin-top: 10px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${myProfile.name}</h1>
          <p>${myProfile.address}</p>
          <p>PHONE: ${myProfile.phone}, PAN: ${myProfile.pan}</p>
        </div>
        <div class="meta-row"><span></span><span>DATED: ${today}</span></div>
        <div class="to-section">
          <p>To,</p><h3>${selectedParty.name}</h3><p>${selectedParty.address || ''}</p><p>PAN: ${selectedParty.panNumber || 'N/A'}</p>
        </div>
        <div class="subject">SUBJECT: CONFIRMATION OF ACCOUNT STATEMENT</div>
        <div class="body-text">
          Dear Sir/Madam,<br/><br/>As per our account record for the fiscal year <strong>${fiscalYear}</strong>, the summary of your account balance is as follows. Please confirm the balance shown below matches your records.
        </div>
        <table class="account-table">
          <tr><th width="40%">Description</th><th width="30%">Amount</th><th width="30%">Dr/Cr</th></tr>
          <tr><td style="text-align: left">Opening Balance</td><td>${fmt(Math.abs(selectedParty.openingBalance || 0))}</td><td>${(selectedParty.openingBalance || 0) < 0 ? 'Dr' : 'Cr'}</td></tr>
          <tr><td style="text-align: left">Total Debit (Sales/Vendor Payments)</td><td>${fmt(selectedParty.sales + selectedParty.payments)}</td><td>Dr</td></tr>
          <tr><td style="text-align: left">Total Credit (Purchases/Customer Payments)</td><td>${fmt(selectedParty.purchases + selectedParty.receipts + selectedParty.returns)}</td><td>Cr</td></tr>
          <tr style="font-weight: bold; background: #f0f0f0;"><td style="text-align: left">Closing Balance</td><td>${fmt(Math.abs(selectedParty.balance))}</td><td>${selectedParty.balance < 0 ? 'Dr (We owe vendor)' : 'Cr (Customer owes us)'}</td></tr>
        </table>
        <div class="body-text">Please check the above details and send one of this Account Confirmation statement duly signed with stamp within 15 days, otherwise we shall understand the above said balance as your acceptance.</div>
        <div class="footer">
          <div class="footer-section"><p><strong>Yours faithfully,</strong></p><p>For ${myProfile.name}</p><div class="stamp-area"><br/><br/>Stamp & Signature</div></div>
          <div class="footer-section"><p><strong>Confirmed by:</strong></p><p>${selectedParty.name}</p><div class="stamp-area"><br/><br/>Stamp & Signature</div></div>
        </div>
      </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  }, [selectedParty, myProfile]);

  // ---------- Action Handlers ----------
  const resetForm = useCallback(() => {
    setName('');
    setPhone('');
    setAddress('');
    setPanNumber('');
    setRegistrationType('Unregistered');
    setOpening(0);
    setOpeningInvoiceNumber('');
    setEditId(null);
  }, []);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (['VAT Registered', 'PAN Registered'].includes(registrationType) && !/^\d{9}$/.test(panNumber)) {
      toast.error(`A 9-digit number is required for ${registrationType}`);
      return;
    }
    const finalPan = registrationType === 'Unregistered' ? '' : panNumber;
    try {
      const payload = { name, phone, address, registrationType, panNumber: finalPan, openingBalance: Number(opening || 0), openingBalanceInvoiceNumber: openingInvoiceNumber };
      if (editId) {
        await API.put(`/parties/${editId}`, payload);
        toast.success('Party updated successfully');
      } else {
        await API.post('/parties', payload);
        toast.success('Party added successfully');
      }
      resetForm();
      setShowPartyFormModal(false);
      fetchList();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || 'Failed to save party');
    }
  }, [name, phone, address, registrationType, panNumber, opening, openingInvoiceNumber, editId, resetForm, fetchList]);

  const openAddPartyModal = useCallback(() => {
    resetForm();
    setShowPartyFormModal(true);
  }, [resetForm]);

  const startEdit = useCallback((p) => {
    setEditId(p._id);
    setName(p.name || '');
    setPhone(p.phone || '');
    setAddress(p.address || '');
    setPanNumber(p.panNumber || '');
    setRegistrationType(p.registrationType || 'Unregistered');
    setOpening(p.openingBalance || 0);
    setOpeningInvoiceNumber(p.openingBalanceInvoiceNumber || '');
    setShowPartyFormModal(true);
  }, []);

  const deleteParty = useCallback(async (id) => {
    if (!window.confirm('Are you sure you want to delete this party?')) return;
    try {
      await API.delete(`/parties/${id}`);
      toast.success('Party deleted');
      fetchList();
    } catch (e) {
      console.error(e);
      toast.error('Failed to delete party');
    }
  }, [fetchList]);

  const openPartyDetails = useCallback(async (p) => {
    try {
      const { data: txs } = await API.get('/transactions');
      setSelectedParty(p);
      setSelectedTxs(txs.filter(t => t.party?._id === p._id));
      setShowModal(true);
      setShowTxForm(false);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load transactions");
    }
  }, []);

  const handleSaveTransaction = useCallback(async (e) => {
    e.preventDefault();
    if (!newTxAmount || Number(newTxAmount) <= 0) return toast.error('Enter a valid amount');
    setTxSubmitting(true);
    try {
      await API.post('/transactions', {
        party: selectedParty._id, type: newTxType, amount: Number(newTxAmount),
        invoiceNumber: newTxInvoice || '', notes: newTxNotes || '', date: newTxDate
      });
      toast.success('Transaction logged successfully');
      setNewTxAmount(''); setNewTxInvoice(''); setNewTxNotes('');
      setShowTxForm(false);
      fetchList();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save transaction');
    } finally {
      setTxSubmitting(false);
    }
  }, [newTxAmount, selectedParty, newTxType, newTxInvoice, newTxNotes, newTxDate, fetchList]);

  const openPartyTxForm = useCallback((p) => {
    openPartyDetails(p);
    setShowTxForm(true);
  }, [openPartyDetails]);

  // ---------- Derived Data ----------
  const summary = useMemo(() => {
    const totals = list.reduce((acc, p) => {
      acc.opening += Number(p.openingBalance || 0);
      acc.purchases += Number(p.purchases || 0);
      acc.payments += Number(p.payments || 0);
      acc.sales += Number(p.sales || 0);
      acc.receipts += Number(p.receipts || 0);
      acc.returns += Number(p.returns || 0);
      acc.balance += Number(p.balance || 0);
      return acc;
    }, { opening: 0, purchases: 0, payments: 0, sales: 0, receipts: 0, returns: 0, balance: 0 });

    return {
      ...totals,
      profitLabel: totals.balance > 0 ? 'Net Receivable (Customers)' : totals.balance < 0 ? 'Net Payable (Vendors)' : 'Balanced',
      totalParties: list.length,
      receivableParties: list.filter(p => p.balance > 0).length,
      payableParties: list.filter(p => p.balance < 0).length,
      settledParties: list.filter(p => p.balance === 0).length
    };
  }, [list]);

  const filteredList = useMemo(() => {
    return list.filter(p => {
      if (filterStatus === 'RECEIVABLE') return p.balance > 0;
      if (filterStatus === 'PAYABLE') return p.balance < 0;
      if (filterStatus === 'SETTLED') return p.balance === 0;
      return true;
    });
  }, [list, filterStatus]);

  // ---------- Memoized Styles ----------
  const styles = useMemo(() => ({
    container: { minHeight: '100vh', background: '#f5f7fa', fontFamily: "'Inter', sans-serif" },
    header: { background: '#ffffff', borderBottom: '1px solid #e1e8ed', padding: isMobile ? '16px' : '20px 32px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 16 : 0, position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)' },
    headerTitle: { display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12 },
    headerIcon: { width: isMobile ? 36 : 40, height: isMobile ? 36 : 40, background: '#1880c2', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 12, fontWeight: 700, letterSpacing: '0.5px' },
    headerText: { fontSize: isMobile ? 18 : 20, fontWeight: 700, color: '#1a202c', letterSpacing: '-0.3px' },
    headerSubtext: { fontSize: 12, color: '#718096', fontWeight: 500, marginTop: 2 },
    headerActions: { display: 'flex', alignItems: 'center', gap: 10, width: isMobile ? '100%' : 'auto', flexWrap: 'wrap' },
    addBtn: { padding: isMobile ? '10px 16px' : '10px 20px', background: '#1880c2', color: 'white', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', flex: isMobile ? 1 : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 },
    main: { maxWidth: 1400, margin: '0 auto', padding: isMobile ? '16px' : '24px 32px' },
    statsRow: { display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? 12 : 16, marginBottom: isMobile ? 20 : 24 },
    statCardLarge: { background: '#ffffff', borderRadius: isMobile ? 8 : 10, padding: isMobile ? 16 : 20, border: '1px solid #e1e8ed', display: 'flex', gap: isMobile ? 12 : 16, alignItems: 'center', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)' },
    statIconBox: { width: isMobile ? 40 : 48, height: isMobile ? 40 : 48, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: isMobile ? 11 : 12, fontWeight: 700, flexShrink: 0, letterSpacing: '0.5px' },
    statContent: { flex: 1 },
    statNumber: { fontSize: isMobile ? 20 : 24, fontWeight: 800, color: '#1a202c' },
    statTitle: { fontSize: isMobile ? 10 : 11, fontWeight: 600, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 2 },
    card: { background: '#ffffff', borderRadius: isMobile ? 8 : 10, border: '1px solid #e1e8ed', marginBottom: isMobile ? 16 : 24, overflow: 'hidden', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)' },
    cardHeader: { padding: isMobile ? '12px 16px' : '16px 24px', borderBottom: '1px solid #e1e8ed', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafbfc' },
    cardTitle: { fontSize: isMobile ? 14 : 16, fontWeight: 700, color: '#1a202c', display: 'flex', alignItems: 'center', gap: 10 },
    cardBody: { padding: isMobile ? 16 : 24 },
    formGrid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: isMobile ? 12 : 16 },
    formGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
    label: { fontSize: 11, fontWeight: 600, color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: { padding: '10px 14px', borderRadius: 6, border: '1px solid #e1e8ed', fontSize: 14, width: '100%', boxSizing: 'border-box', outline: 'none', transition: 'border 0.2s' },
    select: { padding: '10px 14px', borderRadius: 6, border: '1px solid #e1e8ed', fontSize: 14, width: '100%', boxSizing: 'border-box', outline: 'none', background: 'white' },
    btnPrimary: { padding: isMobile ? '10px 20px' : '12px 24px', background: '#1880c2', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, gap: 8, transition: 'all 0.2s' },
    btnSecondary: { padding: isMobile ? '10px 16px' : '10px 20px', background: '#ffffff', color: '#4a5568', border: '1px solid #e1e8ed', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 14 },
    summaryGrid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: isMobile ? 16 : 24, marginBottom: isMobile ? 16 : 24 },
    statsGrid: { display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 12 },
    statCard: { background: '#f8fafc', padding: 12, borderRadius: 6, border: '1px solid #e1e8ed', textAlign: 'center' },
    statLabel: { fontSize: 10, color: '#718096', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' },
    statValue: { fontSize: isMobile ? 14 : 16, fontWeight: 700, color: '#1a202c', marginTop: 4 },
    searchBox: { display: 'flex', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: 12 },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { padding: isMobile ? '10px 12px' : '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#718096', background: '#fafbfc', borderBottom: '1px solid #e1e8ed', textTransform: 'uppercase', letterSpacing: '0.5px' },
    td: { padding: isMobile ? '10px 12px' : '12px 16px', fontSize: isMobile ? 13 : 14, color: '#1a202c', borderBottom: '1px solid #f7fafc' },
    actionBtn: { padding: '6px 12px', borderRadius: 6, border: '1px solid', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginLeft: 6, transition: 'all 0.2s' },
    editBtn: { background: '#e0f2fe', color: '#1880c2', borderColor: '#bae6fd' },
    deleteBtn: { background: '#fef5f5', color: '#991b1b', borderColor: '#fecaca' },
    modal: { position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: isMobile ? 16 : 20 },
    modalContent: { background: '#ffffff', borderRadius: isMobile ? 12 : 16, width: isMobile ? '100%' : isTablet ? '90%' : '800px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', padding: 0, overflow: 'hidden' },
    modalHeader: { display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', gap: 12, borderBottom: '1px solid #e1e8ed', padding: isMobile ? 16 : 24, background: '#fafbfc', flexShrink: 0 },
    modalBody: { padding: isMobile ? 16 : 24, overflowY: 'auto', flex: 1 },
    modalFooter: { borderTop: '1px solid #e1e8ed', padding: isMobile ? 16 : 24, display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'flex-end', background: '#fafbfc', flexShrink: 0 }
  }), [isMobile, isTablet]);

  // --- Show Loading Spinner ONLY if no cached data exists ---
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f5f7fa', gap: 16 }}>
        <div style={{ width: 48, height: 48, border: '4px solid #e0f2fe', borderTopColor: '#1880c2', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <div style={{ fontSize: 16, color: '#718096', fontWeight: 500 }}>Loading parties...</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerTitle}>
          <div style={styles.headerIcon}>PT</div>
          <div>
            <div style={styles.headerText}>Parties & Ledgers</div>
            <div style={styles.headerSubtext}>Accounts receivable/payable management</div>
          </div>
        </div>
        <div style={styles.headerActions}>
          <button
            onClick={openAddPartyModal}
            style={styles.addBtn}
            onMouseEnter={(e) => e.currentTarget.style.background = '#126396'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#1880c2'}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            Add New Party
          </button>
        </div>
      </header>

      <main style={styles.main}>
        {/* STATS */}
        <div style={styles.statsRow}>
          <div style={styles.statCardLarge}>
            <div style={{ ...styles.statIconBox, background: '#e0f2fe', color: '#1880c2' }}>ALL</div>
            <div style={styles.statContent}>
              <div style={styles.statNumber}>{summary.totalParties}</div>
              <div style={styles.statTitle}>Total Parties</div>
            </div>
          </div>
          <div style={styles.statCardLarge}>
            <div style={{ ...styles.statIconBox, background: '#f0fdf4', color: '#166534' }}>RCV</div>
            <div style={styles.statContent}>
              <div style={styles.statNumber}>{summary.receivableParties}</div>
              <div style={styles.statTitle}>Receivables</div>
            </div>
          </div>
          <div style={styles.statCardLarge}>
            <div style={{ ...styles.statIconBox, background: '#fef5f5', color: '#991b1b' }}>PAY</div>
            <div style={styles.statContent}>
              <div style={styles.statNumber}>{summary.payableParties}</div>
              <div style={styles.statTitle}>Payables</div>
            </div>
          </div>
          <div style={styles.statCardLarge}>
            <div style={{ ...styles.statIconBox, background: '#f8fafc', color: '#1880c2' }}>OK</div>
            <div style={styles.statContent}>
              <div style={styles.statNumber}>{summary.settledParties}</div>
              <div style={styles.statTitle}>Settled</div>
            </div>
          </div>
        </div>

        {/* LEDGER & SEARCH */}
        <div style={styles.summaryGrid}>
          <div style={styles.card} ref={ledgerPrintRef}>
            <div style={styles.cardHeader}>
              <div style={styles.cardTitle}>Ledger Summary</div>
            </div>
            <div style={styles.cardBody}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: '#718096', fontWeight: 500 }}>Current Book Balance Status</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#1a202c', marginTop: 4 }}>{summary.profitLabel}</div>
              </div>
              <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>Opening Bal</div>
                  <div style={styles.statValue}>Rs {fmt(summary.opening)}</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>Sales (Dr)</div>
                  <div style={{ ...styles.statValue, color: '#16a34a' }}>Rs {fmt(summary.sales)}</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>Purchases (Cr)</div>
                  <div style={{ ...styles.statValue, color: '#dc2626' }}>Rs {fmt(summary.purchases)}</div>
                </div>
                <div style={{ ...styles.statCard, background: '#fafbfc', borderColor: '#cbd5e0' }}>
                  <div style={styles.statLabel}>Net Dues</div>
                  <div style={{ marginTop: 6 }}>
                    <Badge value={summary.balance} isMobile={isMobile} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={styles.cardTitle}>Search Parties</div>
            </div>
            <div style={styles.cardBody}>
              <div style={styles.searchBox}>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search name, phone, address..."
                  style={{ ...styles.input, flex: 1 }}
                  onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                  onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                />
                <button
                  onClick={fetchList}
                  style={{ ...styles.btnSecondary, width: isMobile ? '100%' : 'auto' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
                >
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ALL PARTIES */}
        <div style={styles.card}>
          <div style={{ ...styles.cardHeader, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: 12 }}>
            <div style={styles.cardTitle}>Registered Parties ({filteredList.length})</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setFilterStatus('ALL')}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: filterStatus === 'ALL' ? '1px solid #1880c2' : '1px solid #e1e8ed',
                  background: filterStatus === 'ALL' ? '#1880c2' : '#fff',
                  color: filterStatus === 'ALL' ? '#fff' : '#4a5568'
                }}
              >
                All ({summary.totalParties})
              </button>
              <button
                type="button"
                onClick={() => setFilterStatus('RECEIVABLE')}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: filterStatus === 'RECEIVABLE' ? '1px solid #166534' : '1px solid #e1e8ed',
                  background: filterStatus === 'RECEIVABLE' ? '#f0fdf4' : '#fff',
                  color: filterStatus === 'RECEIVABLE' ? '#166534' : '#4a5568'
                }}
              >
                Receivables ({summary.receivableParties})
              </button>
              <button
                type="button"
                onClick={() => setFilterStatus('PAYABLE')}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: filterStatus === 'PAYABLE' ? '1px solid #991b1b' : '1px solid #e1e8ed',
                  background: filterStatus === 'PAYABLE' ? '#fef5f5' : '#fff',
                  color: filterStatus === 'PAYABLE' ? '#991b1b' : '#4a5568'
                }}
              >
                Payables ({summary.payableParties})
              </button>
              <button
                type="button"
                onClick={() => setFilterStatus('SETTLED')}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: filterStatus === 'SETTLED' ? '1px solid #1880c2' : '1px solid #e1e8ed',
                  background: filterStatus === 'SETTLED' ? '#e0f2fe' : '#fff',
                  color: filterStatus === 'SETTLED' ? '#1880c2' : '#4a5568'
                }}
              >
                Settled ({summary.settledParties})
              </button>
            </div>
          </div>
          {!isMobile && !isTablet ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, width: 50 }}>#</th>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Phone</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Opening Bal</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Credit Sales</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Purchases</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Book Balance</th>
                    <th style={{ ...styles.th, textAlign: 'center', width: 210 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredList.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', padding: 40, color: '#718096' }}>
                        No matching parties found.
                      </td>
                    </tr>
                  ) : (
                    filteredList.map((p, i) => (
                      <tr
                        key={p._id}
                        onClick={() => openPartyDetails(p)}
                        style={{ cursor: 'pointer', transition: 'background 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f4f9fd'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={styles.td}>{i + 1}</td>
                        <td style={{ ...styles.td, fontWeight: 600 }}>{p.name}</td>
                        <td style={styles.td}>{p.phone || '—'}</td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>Rs {fmt(p.openingBalance)}</td>
                        <td style={{ ...styles.td, textAlign: 'right', color: '#16a34a' }}>Rs {fmt(p.sales)}</td>
                        <td style={{ ...styles.td, textAlign: 'right', color: '#dc2626' }}>Rs {fmt(p.purchases)}</td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>
                          <Badge value={p.balance} isMobile={isMobile} />
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => openPartyTxForm(p)}
                            style={{ ...styles.actionBtn, background: '#f0fdf4', color: '#166534', borderColor: '#bbf7d0' }}
                            title="Log payment or receipt for this party"
                          >
                            + Tx
                          </button>
                          <button
                            onClick={() => startEdit(p)}
                            style={{ ...styles.actionBtn, ...styles.editBtn }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#bae6fd'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#e0f2fe'}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteParty(p._id)}
                            style={{ ...styles.actionBtn, ...styles.deleteBtn }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#fef5f5'}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: 16 }}>
              {filteredList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#718096' }}>
                  No matching parties found.
                </div>
              ) : (
                filteredList.map((p, i) => (
                  <MobilePartyCard 
                    key={p._id} 
                    party={p} 
                    index={i} 
                    isMobile={isMobile} 
                    onOpenDetails={openPartyDetails} 
                    onEdit={startEdit} 
                    onDelete={deleteParty} 
                  />
                ))
              )}
            </div>
          )}
        </div>
      </main>

      {/* ADD/EDIT PARTY MODAL */}
      {showPartyFormModal && (
        <div style={styles.modal} onClick={() => setShowPartyFormModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#1a202c' }}>
                  {editId ? 'Edit Party Account' : 'Register New Party'}
                </h2>
                <div style={{ fontSize: 12, color: '#718096', marginTop: 4 }}>
                  {editId ? 'Update party information' : 'Add new customer or vendor'}
                </div>
              </div>
            </div>
            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div style={styles.modalBody}>
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Party Name *</label>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter party name"
                      style={styles.input}
                      required
                      onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                      onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Phone Number</label>
                    <input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="Enter phone number"
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                      onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                    />
                  </div>
                  <div style={{ ...styles.formGroup, gridColumn: isMobile ? 'span 1' : 'span 2' }}>
                    <label style={styles.label}>Address</label>
                    <input
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Enter address"
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                      onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Tax Registration *</label>
                    <select
                      value={registrationType}
                      onChange={(e) => {
                        setRegistrationType(e.target.value);
                        if (e.target.value === 'Unregistered') setPanNumber('');
                      }}
                      style={styles.select}
                    >
                      <option value="Unregistered">Unregistered (Cash/Local)</option>
                      <option value="PAN Registered">PAN Registered (Non-VAT)</option>
                      <option value="VAT Registered">VAT Registered (13% VAT)</option>
                    </select>
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={{ ...styles.label, color: registrationType !== 'Unregistered' ? '#1880c2' : '#4a5568' }}>
                      {registrationType === 'VAT Registered' ? 'VAT Number *' : registrationType === 'PAN Registered' ? 'PAN Number *' : 'PAN (Optional)'}
                    </label>
                    <input
                      value={panNumber}
                      onChange={(e) => setPanNumber(e.target.value.replace(/\D/g, '').slice(0, 9))}
                      placeholder={registrationType !== 'Unregistered' ? "Must be 9 digits" : "Leave blank if none"}
                      style={{ 
                        ...styles.input, 
                        background: registrationType === 'Unregistered' ? '#f8fafc' : 'white',
                        borderColor: (registrationType !== 'Unregistered' && panNumber.length !== 9) ? '#fecaca' : '#e1e8ed'
                      }}
                      maxLength={9}
                      disabled={registrationType === 'Unregistered'}
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Opening Balance</label>
                    <input
                      type="number"
                      step="0.01"
                      value={opening}
                      onChange={(e) => setOpening(e.target.value)}
                      placeholder="0.00"
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                      onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                    />
                  </div>
                  <div style={{ ...styles.formGroup, gridColumn: isMobile ? 'span 1' : 'span 2' }}>
                    <label style={styles.label}>Opening Invoice Number</label>
                    <input
                      type="text"
                      value={openingInvoiceNumber}
                      onChange={(e) => setOpeningInvoiceNumber(e.target.value)}
                      placeholder="Invoice # for opening balance"
                      style={styles.input}
                      onFocus={(e) => e.target.style.borderColor = '#1880c2'}
                      onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                    />
                  </div>
                </div>
              </div>
              <div style={styles.modalFooter}>
                <button
                  type="button"
                  onClick={() => setShowPartyFormModal(false)}
                  style={{ ...styles.btnSecondary, background: '#fef5f5', color: '#991b1b', borderColor: '#fecaca' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={styles.btnPrimary}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#126396'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#1880c2'}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M13 3L6 10L3 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {editId ? 'Update Party' : 'Register Party'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* LEDGER DETAILS MODAL */}
      {showModal && selectedParty && (
        <div style={styles.modal} onClick={() => setShowModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()} ref={partyPrintRef}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={{ margin: 0, color: '#1a202c', fontSize: isMobile ? 16 : 18, fontWeight: 700 }}>{selectedParty.name}</h2>
                <div style={{ fontSize: 12, color: '#718096', marginTop: 4 }}>
                  {selectedParty.phone || 'No Phone'} • {selectedParty.address || 'No Address'} • {selectedParty.registrationType || 'Unregistered'}: {selectedParty.panNumber || 'N/A'}
                </div>
              </div>
              <div style={{ textAlign: isMobile ? 'left' : 'right', marginTop: isMobile ? 8 : 0 }}>
                <div style={{ fontSize: 11, color: '#718096', fontWeight: 600 }}>NET BALANCE DUE</div>
                <div style={{ marginTop: 4 }}>
                  <Badge value={selectedParty.balance} isMobile={isMobile} />
                </div>
              </div>
            </div>

            <div style={styles.modalBody}>
              <div style={{ ...styles.statsGrid, marginBottom: 24 }}>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>Opening</div>
                  <div style={styles.statValue}>Rs {fmt(selectedParty.openingBalance)}</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>Credit Sales</div>
                  <div style={{ ...styles.statValue, color: '#16a34a' }}>Rs {fmt(selectedParty.sales)}</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>Purchases</div>
                  <div style={{ ...styles.statValue, color: '#dc2626' }}>Rs {fmt(selectedParty.purchases)}</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>Cash Txs</div>
                  <div style={{ ...styles.statValue, color: '#1880c2' }}>Rs {fmt(selectedParty.payments + selectedParty.receipts)}</div>
                </div>
              </div>

              {/* MANUAL PAYMENT/RECEIPT RECORD FORM */}
              <div style={{ marginBottom: 24, border: '1px solid #e1e8ed', borderRadius: 8, overflow: 'hidden' }}>
                <div
                  onClick={() => setShowTxForm(!showTxForm)}
                  style={{
                    background: '#fafbfc',
                    padding: '12px 16px',
                    borderBottom: showTxForm ? '1px solid #e1e8ed' : 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <strong style={{ fontSize: 13, color: '#1a202c', fontWeight: 600 }}>Record Payment / Cash Receipt Transaction</strong>
                  <span style={{ fontSize: 11, color: '#1880c2', fontWeight: 600 }}>
                    {showTxForm ? '▲ Hide Form' : '▼ Show Form'}
                  </span>
                </div>
                {showTxForm && (
                  <form onSubmit={handleSaveTransaction} style={{ padding: 16, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 12 }}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Transaction Type</label>
                      <select value={newTxType} onChange={e => setNewTxType(e.target.value)} style={styles.select}>
                        <option value="receipt">Cash Receipt (From Customer)</option>
                        <option value="payment">Cash Payment (To Vendor/Supplier)</option>
                      </select>
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Amount (Rs)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={newTxAmount}
                        onChange={e => setNewTxAmount(e.target.value)}
                        placeholder="Amount in Rupees"
                        style={styles.input}
                        required
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Date</label>
                      <input type="date" value={newTxDate} onChange={e => setNewTxDate(e.target.value)} style={styles.input} required />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Ref. Invoice #</label>
                      <input value={newTxInvoice} onChange={e => setNewTxInvoice(e.target.value)} placeholder="e.g. INV-123" style={styles.input} />
                    </div>
                    <div style={{ ...styles.formGroup, gridColumn: isMobile ? 'span 1' : 'span 2' }}>
                      <label style={styles.label}>Particulars / Notes</label>
                      <input value={newTxNotes} onChange={e => setNewTxNotes(e.target.value)} placeholder="e.g. Cash received, bank check..." style={styles.input} />
                    </div>
                    <div style={{ gridColumn: isMobile ? 'span 1' : 'span 2', textAlign: 'right', marginTop: 8 }}>
                      <button type="submit" style={styles.btnPrimary} disabled={txSubmitting}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#126396'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#1880c2'}
                      >
                        {txSubmitting ? 'Logging...' : 'Log Transaction'}
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* ACCOUNT STATEMENT LEDGER */}
              <h3 style={{ fontSize: isMobile ? 14 : 16, fontWeight: 700, marginBottom: 16, color: '#1a202c' }}>Account Statement Ledger</h3>
              <div style={{ overflowX: 'auto', border: '1px solid #e1e8ed', borderRadius: 8 }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Date</th>
                      <th style={styles.th}>Particulars</th>
                      {!isMobile && <th style={styles.th}>Invoice/Ref</th>}
                      <th style={{ ...styles.th, textAlign: 'right', background: '#fef5f5' }}>Dr. (Debit)</th>
                      <th style={{ ...styles.th, textAlign: 'right', background: '#f0fdf4' }}>Cr. (Credit)</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>Running Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const sortedTxs = [...selectedTxs].sort((a, b) => new Date(a.date) - new Date(b.date));
                      let runningBalance = Number(selectedParty.openingBalance || 0);
                      const rows = [];
                      const openingDate = sortedTxs.length > 0 ? new Date(sortedTxs[0].date).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN');

                      // Opening Balance row
                      rows.push(
                        <tr key="opening" style={{ background: '#f4f9fd' }}>
                          <td style={styles.td}>{openingDate}</td>
                          <td style={{ ...styles.td, fontWeight: 600 }}>Opening Balance</td>
                          {!isMobile && <td style={styles.td}>{selectedParty.openingBalanceInvoiceNumber || '—'}</td>}
                          <td style={{ ...styles.td, textAlign: 'right' }}>—</td>
                          <td style={{ ...styles.td, textAlign: 'right' }}>—</td>
                          <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, fontSize: isMobile ? 11 : 14 }}>Rs {fmt(runningBalance)}</td>
                        </tr>
                      );

                      // Transaction rows
                      sortedTxs.forEach((t) => {
                        const amount = Number(t.amount || 0);
                        const isDebit = t.type === 'sale' || t.type === 'payment';
                        const isCredit = t.type === 'purchase' || t.type === 'receipt';

                        let isReturnDebit = false;
                        let isReturnCredit = false;

                        if (t.type === 'return') {
                          if (t.invoiceNumber?.startsWith('RET-') || t.notes?.toLowerCase().includes('purchase return')) {
                            isReturnDebit = true; 
                          } else {
                            isReturnCredit = true; 
                          }
                        }

                        if (isDebit || isReturnDebit) {
                          runningBalance += amount;
                        } else if (isCredit || isReturnCredit) {
                          runningBalance -= amount;
                        }

                        let label = t.type.toUpperCase();
                        if (t.type === 'sale') label = 'Credit Sale';
                        if (t.type === 'receipt') label = 'Cash Receipt';
                        if (t.type === 'payment') label = 'Vendor Payment';
                        if (t.type === 'purchase') label = 'Procurement Pur.';
                        if (t.type === 'return') {
                          label = isReturnDebit ? 'Purchase Return' : 'Sales Return'; 
                        }

                        rows.push(
                          <tr key={t._id}>
                            <td style={{ ...styles.td, fontSize: isMobile ? 11 : 14 }}>{new Date(t.date).toLocaleDateString('en-IN')}</td>
                            <td style={{ ...styles.td, fontWeight: 600, fontSize: isMobile ? 11 : 14 }}>{label}</td>
                            {!isMobile && <td style={styles.td}>{t.invoiceNumber || '—'}</td>}
                            <td style={{
                              ...styles.td,
                              textAlign: 'right',
                              color: (isDebit || isReturnDebit) ? '#16a34a' : '#94a3b8',
                              fontWeight: (isDebit || isReturnDebit) ? 600 : 400,
                              fontSize: isMobile ? 11 : 14
                            }}>
                              {(isDebit || isReturnDebit) ? `Rs ${fmt(amount)}` : '—'}
                            </td>
                            <td style={{
                              ...styles.td,
                              textAlign: 'right',
                              color: (isCredit || isReturnCredit) ? '#dc2626' : '#94a3b8',
                              fontWeight: (isCredit || isReturnCredit) ? 600 : 400,
                              fontSize: isMobile ? 11 : 14
                            }}>
                              {(isCredit || isReturnCredit) ? `Rs ${fmt(amount)}` : '—'}
                            </td>
                            <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, fontSize: isMobile ? 11 : 14 }}>
                              Rs {fmt(runningBalance)}
                            </td>
                          </tr>
                        );
                      });

                      // Closing Balance row
                      rows.push(
                        <tr key="closing" style={{ background: '#f0fdf4' }}>
                          <td style={{ ...styles.td, fontWeight: 700 }}>{new Date().toLocaleDateString('en-IN')}</td>
                          <td style={{ ...styles.td, fontWeight: 700 }}>Closing Balance</td>
                          {!isMobile && <td style={{ ...styles.td, fontWeight: 700 }}>—</td>}
                          <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>—</td>
                          <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>—</td>
                          <td style={{ ...styles.td, textAlign: 'right' }}>
                            <Badge value={runningBalance} isMobile={isMobile} />
                          </td>
                        </tr>
                      );
                      return rows;
                    })()}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                onClick={printConfirmation}
                style={{ ...styles.btnPrimary, background: '#1880c2', flex: isMobile ? 1 : 'none' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#126396'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#1880c2'}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M4 6V2h8v4M4 12H3a1 1 0 01-1-1V8a1 1 0 011-1h10a1 1 0 011 1v3a1 1 0 01-1 1h-1M4 10h8v4H4v-4z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Confirmation
              </button>
              <button
                onClick={() => printRefContent(partyPrintRef, `${selectedParty.name} - Statement`)}
                style={{ ...styles.btnPrimary, background: '#16a34a', flex: isMobile ? 1 : 'none' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#15803d'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#16a34a'}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M9 2H4a1 1 0 00-1 1v10a1 1 0 001 1h8a1 1 0 001-1V6L9 2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M9 2v4h4M10 9H6M10 11H6M7 7H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Export PDF
              </button>
              <button
                onClick={() => setShowModal(false)}
                style={{ ...styles.btnSecondary, background: '#fef5f5', color: '#991b1b', borderColor: '#fecaca', flex: isMobile ? 1 : 'none' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#fef5f5'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}